import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { FileText, Upload, Search, Trash2, Download, Pencil, Check, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatBytes = (n) => {
  if (!n && n !== 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getFullYear()}`;
};

const CaseDocuments = ({ caseId }) => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingDescription, setEditingDescription] = useState('');
  const [pendingDescription, setPendingDescription] = useState('');
  const fileInputRef = useRef(null);

  const fetchDocs = async () => {
    if (!caseId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/case-documents/case/${caseId}`);
      setDocs(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Failed to load documents:', err);
      toast.error('Σφάλμα κατά τη φόρτωση εγγράφων');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocs();
  }, [caseId]);

  const onPickFile = () => fileInputRef.current?.click();

  const onFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    if (!caseId) {
      toast.error('Το έγγραφο πρέπει να σχετίζεται με υπόθεση');
      e.target.value = '';
      return;
    }

    setUploading(true);
    let successCount = 0;
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append('case_id', caseId);
        fd.append('file', file);
        fd.append('description', pendingDescription || '');
        await axios.post(`${API}/case-documents/upload`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        successCount += 1;
      } catch (err) {
        const msg = err.response?.data?.detail || err.message || 'Σφάλμα';
        toast.error(`${file.name}: ${msg}`);
      }
    }
    if (successCount > 0) {
      toast.success(`Αποθηκεύτηκαν ${successCount} ${successCount === 1 ? 'έγγραφο' : 'έγγραφα'}`);
      setPendingDescription('');
      fetchDocs();
    }
    setUploading(false);
    e.target.value = '';
  };

  const handleDownload = async (doc) => {
    try {
      const res = await axios.get(`${API}/case-documents/${doc.id}/download`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: doc.content_type || 'application/octet-stream' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.filename || 'document';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (err) {
      console.error(err);
      toast.error('Σφάλμα κατά τη λήψη');
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Διαγραφή του "${doc.filename}";`)) return;
    try {
      await axios.delete(`${API}/case-documents/${doc.id}`);
      setDocs((d) => d.filter((x) => x.id !== doc.id));
      toast.success('Το έγγραφο διαγράφηκε');
    } catch (err) {
      toast.error('Σφάλμα κατά τη διαγραφή');
    }
  };

  const startEdit = (doc) => {
    setEditingId(doc.id);
    setEditingDescription(doc.description || '');
  };

  const saveEdit = async (doc) => {
    try {
      await axios.patch(`${API}/case-documents/${doc.id}`, { description: editingDescription });
      setDocs((arr) => arr.map((x) => (x.id === doc.id ? { ...x, description: editingDescription } : x)));
      setEditingId(null);
      toast.success('Η περιγραφή ενημερώθηκε');
    } catch (err) {
      toast.error('Σφάλμα κατά την αποθήκευση');
    }
  };

  // Client-side filtering by filename or description
  const filtered = docs.filter((d) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (d.filename || '').toLowerCase().includes(q) ||
      (d.description || '').toLowerCase().includes(q)
    );
  });

  return (
    <div className="bg-slate-50/80 border border-slate-200 rounded-xl p-3 sm:p-4 mt-4" data-testid="case-documents-section">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-sm sm:text-base tracking-tight text-slate-900 flex items-center gap-2">
          <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-red-700" />
          Έγγραφα υπόθεσης
        </h3>
        <span className="text-xs text-slate-500">{docs.length} αρχεία</span>
      </div>

      <p className="text-xs text-slate-600 mb-3">
        Αποθηκεύστε τα έγγραφα που σχετίζονται με την υπόθεση. Μπορείτε να τα αναζητήσετε και να τα κατεβάσετε ξανά οποτεδήποτε.
      </p>

      {/* Upload row */}
      <div className="flex flex-col sm:flex-row gap-2 mb-3">
        <Input
          type="text"
          placeholder="Σύντομη περιγραφή (προαιρετικό) — π.χ. ΑΓΩΓΗ ΠΡΩΤΟΤΥΠΟ"
          value={pendingDescription}
          onChange={(e) => setPendingDescription(e.target.value)}
          className="h-10 bg-white border-slate-300 text-sm flex-1"
          data-testid="document-description-input"
        />
        <Button
          onClick={onPickFile}
          disabled={uploading || !caseId}
          className="h-10 rounded-lg bg-red-700 hover:bg-red-800 text-white text-sm whitespace-nowrap shadow-md shadow-red-900/20"
          data-testid="document-upload-btn"
        >
          <Upload className="w-4 h-4 mr-1.5" />
          {uploading ? 'Μεταφόρτωση...' : 'Επιλογή αρχείων'}
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={onFileChange}
          className="hidden"
          data-testid="document-file-input"
        />
      </div>

      {/* Search row (only when there are docs) */}
      {docs.length > 0 && (
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            type="text"
            placeholder="Αναζήτηση εγγράφων..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-10 pl-9 bg-white border-slate-300 text-sm"
            data-testid="document-search-input"
          />
        </div>
      )}

      {/* List */}
      {loading ? (
        <p className="text-xs text-slate-500 text-center py-4">Φόρτωση...</p>
      ) : filtered.length === 0 ? (
        <p className="text-xs text-slate-500 text-center py-4">
          {docs.length === 0 ? 'Δεν έχουν αποθηκευτεί έγγραφα ακόμη' : 'Κανένα έγγραφο δεν ταιριάζει στην αναζήτηση'}
        </p>
      ) : (
        <div className="space-y-2" data-testid="document-list">
          {filtered.map((doc) => (
            <div
              key={doc.id}
              className="bg-white border border-slate-200 rounded-md p-3 flex flex-col sm:flex-row sm:items-center gap-2"
              data-testid={`document-item-${doc.id}`}
            >
              <div className="flex-1 min-w-0">
                <button
                  onClick={() => handleDownload(doc)}
                  className="text-sm font-medium text-slate-900 hover:text-red-700 truncate text-left block w-full"
                  data-testid={`document-name-${doc.id}`}
                >
                  {doc.filename}
                </button>

                {/* Description: editable inline */}
                {editingId === doc.id ? (
                  <div className="flex items-center gap-1 mt-1">
                    <Input
                      type="text"
                      value={editingDescription}
                      onChange={(e) => setEditingDescription(e.target.value)}
                      className="h-7 bg-white border-slate-300 text-xs"
                      autoFocus
                    />
                    <button onClick={() => saveEdit(doc)} className="p-1 text-emerald-600 hover:bg-emerald-50 rounded">
                      <Check className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => setEditingId(null)} className="p-1 text-slate-500 hover:bg-slate-100 rounded">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-1 mt-0.5">
                    <p className="text-xs text-slate-600 truncate">
                      {doc.description || <span className="italic text-slate-400">χωρίς περιγραφή</span>}
                    </p>
                    <button
                      onClick={() => startEdit(doc)}
                      className="text-slate-400 hover:text-slate-700"
                      title="Επεξεργασία περιγραφής"
                    >
                      <Pencil className="w-3 h-3" />
                    </button>
                  </div>
                )}

                <p className="text-[11px] text-slate-500 mt-0.5">
                  {formatBytes(doc.size)} · {formatDate(doc.created_at)}
                </p>
              </div>

              <div className="flex items-center gap-1 self-end sm:self-auto">
                <button
                  onClick={() => handleDownload(doc)}
                  className="p-1.5 text-slate-600 hover:bg-slate-100 rounded"
                  title="Λήψη"
                  data-testid={`document-download-${doc.id}`}
                >
                  <Download className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDelete(doc)}
                  className="p-1.5 text-rose-600 hover:bg-rose-50 rounded"
                  title="Διαγραφή"
                  data-testid={`document-delete-${doc.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CaseDocuments;
